import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { SESSIONS_COLLECTION, firestore } from "../lib/firebase";
import type {
  ExpectedParticipant,
  SessionShortCodeAccessRequest,
  SessionShortCodeAccessRequestDraft,
  SessionShortCodeLobby,
  SessionShortCodeLobbyParticipant,
  TrainingSession,
} from "../types/session";
import { asRecord, normalizeEmail, toDate } from "../utils/firestoreData";
import { writeAuditEvent } from "./auditService";
import {
  listExpectedParticipants,
  readTrainingSession,
} from "./sessionService";

const SESSION_SHORT_CODES_COLLECTION = "sessionShortCodes";
const SHORT_CODE_PARTICIPANTS_COLLECTION = "participants";
const SHORT_CODE_ACCESS_REQUESTS_COLLECTION = "accessRequests";

function normalizeShortCode(value: string): string {
  return value.replace(/\s+/g, "").trim().toUpperCase();
}

function shortCodeRef(shortCode: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_SHORT_CODES_COLLECTION,
    normalizeShortCode(shortCode),
  );
}

function shortCodeParticipantRef(shortCode: string, participantSlotId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_SHORT_CODES_COLLECTION,
    normalizeShortCode(shortCode),
    SHORT_CODE_PARTICIPANTS_COLLECTION,
    participantSlotId,
  );
}

function shortCodeAccessRequestRef(shortCode: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    collection(
      firestore,
      SESSION_SHORT_CODES_COLLECTION,
      normalizeShortCode(shortCode),
      SHORT_CODE_ACCESS_REQUESTS_COLLECTION,
    ),
  );
}

function sessionDocRef(sessionId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSIONS_COLLECTION, sessionId);
}

function maskEmail(email: string): string | null {
  const normalized = normalizeEmail(email);

  if (!normalized) {
    return null;
  }

  const [localPart, domain] = normalized.split("@");

  if (!localPart || !domain) {
    return null;
  }

  const visibleLocal = localPart.slice(0, 1);
  return `${visibleLocal}${"*".repeat(Math.max(2, localPart.length - 1))}@${domain}`;
}

function participantProjection(
  participant: ExpectedParticipant,
  order: number,
): SessionShortCodeLobbyParticipant {
  return {
    emailMasked: maskEmail(participant.email),
    firstName: participant.firstName,
    id: participant.id,
    lastName: participant.lastName,
    onboardingStatus: participant.onboardingStatus,
    order,
  };
}

function normalizeLobby(
  id: string,
  data: Record<string, unknown>,
  participants: SessionShortCodeLobbyParticipant[],
): SessionShortCodeLobby {
  return {
    id,
    participants,
    publicEndAt: toDate(data.publicEndAt),
    publicStartAt: toDate(data.publicStartAt),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    status: "active",
    timezone: typeof data.timezone === "string" ? data.timezone : "Europe/Paris",
    title: typeof data.title === "string" ? data.title : "Session",
  };
}

function normalizeLobbyParticipant(
  id: string,
  data: Record<string, unknown>,
): SessionShortCodeLobbyParticipant {
  return {
    emailMasked: typeof data.emailMasked === "string" ? data.emailMasked : null,
    firstName: typeof data.firstName === "string" ? data.firstName : "",
    id: typeof data.id === "string" ? data.id : id,
    lastName: typeof data.lastName === "string" ? data.lastName : "",
    onboardingStatus:
      data.onboardingStatus === "pending_auth" ||
      data.onboardingStatus === "linked" ||
      data.onboardingStatus === "access_requested" ||
      data.onboardingStatus === "disabled"
        ? data.onboardingStatus
        : "not_started",
    order: typeof data.order === "number" ? data.order : 0,
  };
}

function normalizeAccessRequest(
  id: string,
  data: Record<string, unknown>,
): SessionShortCodeAccessRequest {
  return {
    createdAt: toDate(data.createdAt),
    email: typeof data.email === "string" ? data.email : "",
    firstName: typeof data.firstName === "string" ? data.firstName : "",
    id: typeof data.id === "string" ? data.id : id,
    lastName: typeof data.lastName === "string" ? data.lastName : "",
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : null,
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    status: "pending",
  };
}

export async function activateSessionShortCode({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<TrainingSession> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  if (session.status !== "active") {
    throw new Error("La session doit être active avant d'activer la clé courte.");
  }

  const shortCode = normalizeShortCode(session.shortCode);

  if (!shortCode) {
    throw new Error("La session ne dispose pas de clé courte.");
  }

  const participants = await listExpectedParticipants(session.id);
  const projections = participants
    .slice()
    .sort((left, right) =>
      `${left.lastName} ${left.firstName}`.localeCompare(
        `${right.lastName} ${right.firstName}`,
        "fr",
      ),
    )
    .map(participantProjection);
  const rootBatch = writeBatch(firestore);

  rootBatch.update(sessionDocRef(session.id), {
    shortCodeActivatedAt: serverTimestamp(),
    shortCodeStatus: "active",
    updatedAt: serverTimestamp(),
  });
  rootBatch.set(
    shortCodeRef(shortCode),
    {
      activatedAt: serverTimestamp(),
      activatedBy: actorUid,
      id: shortCode,
      participantCount: projections.length,
      publicEndAt: session.endAt,
      publicStartAt: session.startAt,
      sessionId: session.id,
      status: "active",
      timezone: session.timezone,
      title: session.title,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  await rootBatch.commit();

  if (projections.length > 0) {
    const participantsBatch = writeBatch(firestore);
    for (const projection of projections) {
      participantsBatch.set(shortCodeParticipantRef(shortCode, projection.id), {
        ...projection,
        sessionId: session.id,
        updatedAt: serverTimestamp(),
      });
    }
    await participantsBatch.commit();
  }

  await writeAuditEvent({
    actorUid,
    eventType: "session.short_code.activated",
    metadata: {
      participantCount: projections.length,
      sessionId: session.id,
      shortCode,
    },
  });

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après activation de la clé courte.");
  }

  return nextSession;
}

export async function disableSessionShortCode({
  actorUid,
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<TrainingSession> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const shortCode = normalizeShortCode(session.shortCode);
  const batch = writeBatch(firestore);

  batch.update(sessionDocRef(session.id), {
    shortCodeDisabledAt: serverTimestamp(),
    shortCodeStatus: "disabled",
    updatedAt: serverTimestamp(),
  });
  batch.set(
    shortCodeRef(shortCode),
    {
      disabledAt: serverTimestamp(),
      status: "disabled",
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  await batch.commit();

  await writeAuditEvent({
    actorUid,
    eventType: "session.short_code.disabled",
    metadata: {
      sessionId: session.id,
      shortCode,
    },
  });

  const nextSession = await readTrainingSession(session.id);

  if (!nextSession) {
    throw new Error("Session introuvable après désactivation de la clé courte.");
  }

  return nextSession;
}

export async function resolveSessionShortCode(
  input: string,
): Promise<SessionShortCodeLobby | null> {
  if (!firestore) {
    return null;
  }

  const shortCode = normalizeShortCode(input);

  if (!shortCode) {
    return null;
  }

  const rootSnapshot = await getDoc(shortCodeRef(shortCode));

  if (!rootSnapshot.exists()) {
    return null;
  }

  const rootData = asRecord(rootSnapshot.data());

  if (rootData.status !== "active") {
    return null;
  }

  const participantsSnapshot = await getDocs(
    query(
      collection(
        firestore,
        SESSION_SHORT_CODES_COLLECTION,
        shortCode,
        SHORT_CODE_PARTICIPANTS_COLLECTION,
      ),
      orderBy("order", "asc"),
    ),
  );
  const participants = participantsSnapshot.docs.map((participantDoc) =>
    normalizeLobbyParticipant(
      participantDoc.id,
      asRecord(participantDoc.data()),
    ),
  );

  return normalizeLobby(shortCode, rootData, participants);
}

export async function createSessionShortCodeAccessRequest({
  draft,
  shortCode,
}: {
  draft: SessionShortCodeAccessRequestDraft;
  shortCode: string;
}): Promise<SessionShortCodeAccessRequest> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const normalizedShortCode = normalizeShortCode(shortCode);
  const lobby = await resolveSessionShortCode(normalizedShortCode);

  if (!lobby) {
    throw new Error("Clé courte invalide ou inactive.");
  }

  const requestRef = shortCodeAccessRequestRef(normalizedShortCode);
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const email = normalizeEmail(draft.email);

  await setDoc(requestRef, {
    createdAt: serverTimestamp(),
    email,
    firstName,
    id: requestRef.id,
    lastName,
    participantSlotId: draft.participantSlotId,
    sessionId: lobby.sessionId,
    status: "pending",
  });

  const snapshot = await getDoc(requestRef);
  return normalizeAccessRequest(requestRef.id, asRecord(snapshot.data()));
}

export async function listSessionShortCodeAccessRequests(
  input: string,
): Promise<SessionShortCodeAccessRequest[]> {
  if (!firestore) {
    return [];
  }

  const shortCode = normalizeShortCode(input);

  if (!shortCode) {
    return [];
  }

  const requestsSnapshot = await getDocs(
    query(
      collection(
        firestore,
        SESSION_SHORT_CODES_COLLECTION,
        shortCode,
        SHORT_CODE_ACCESS_REQUESTS_COLLECTION,
      ),
      orderBy("createdAt", "desc"),
    ),
  );

  return requestsSnapshot.docs.map((requestDoc) =>
    normalizeAccessRequest(requestDoc.id, asRecord(requestDoc.data())),
  );
}
